import { Injectable } from '@angular/core';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {ElementVisibility, InternalConfig} from '../../core/services/impl/configuration/internal-config';
import {env, isMobile, salesChannel, updateOrCreateFavicon} from '../../core/utils/utils';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {ProductTileKey} from '../enums/product-tile-key.enum';
import {OtherProductConfig} from '../models/other-product-config';
import {RuntimeEnvironment} from '../enums/runtime-environment.enum';
import {QueryParamName} from '../../core/enums/query-param-name.enum';

@Injectable({
  providedIn: 'root'
})
export class InternalConfigService {

  get isMobile(): boolean {
    return isMobile();
  }

  get otherProductsVisible(): boolean {
    const config = InternalConfig.config[salesChannel()].otherProductsConfig;
    return isNotNullOrUndefined(config) && config.enabled;
  }

  get accumulatedSectionsSteps(): string[] {
    const config = InternalConfig.config[salesChannel()];
    return config ? config.accumulatedSectionsSteps : null;
  }

  constructor(private urlParamsService: UrlParamsService) {
    this.setFavicon();
  }

  resolveSectionVisibility(sectionProperty: string): boolean {
    const sectionsVisibilityConfig = InternalConfig.config[salesChannel()].sectionsVisibilityConfig;
    if (isNotNullOrUndefined(sectionsVisibilityConfig) && sectionsVisibilityConfig.hasOwnProperty(sectionProperty)) {
      return this.resolveElementVisibility(sectionsVisibilityConfig[sectionProperty]);
    }
    return true;
  }

  resolveElementVisibility(elementConfig: any): boolean {
    if (!isNotNullOrUndefined(elementConfig)) {
      return false;
    }
    if (elementConfig.hasOwnProperty('visible')) {
      const origin = this.urlParamsService.getQueryParam(QueryParamName.ORIGIN);
      switch (elementConfig.visible) {
        case ElementVisibility.HIDDEN:
          return false;
        case ElementVisibility.ONLY_MOBILE:
          return this.isMobile && !(origin && origin.toUpperCase() === 'MOBILE');
        case ElementVisibility.ONLY_DESKTOP:
          return !this.isMobile;
        case ElementVisibility.VISIBLE:
          return true;
        case ElementVisibility.ORIGIN_WWW:
          return origin ? origin.toUpperCase() === 'WWW' : false;
        case ElementVisibility.ORIGIN_NOT_MOBILE:
          return origin ? origin.toUpperCase() !== 'MOBILE' : true;
      }
    } else {
      console.error('Visibility for given element is not defined', elementConfig);
      return false;
    }
  }

  getProductUrl(productKey: ProductTileKey): string {
    const otherProductsConfig = InternalConfig.config[salesChannel()].otherProductsConfig;
    if (isNotNullOrUndefined(otherProductsConfig)) {
      const products: OtherProductConfig[] = otherProductsConfig.products;
      const urlSuffix = `&source=${otherProductsConfig.urlSource}`;
      const productConfig = products.find(config => config.productKey === productKey);
      return productConfig ? `${this.getProductEnvUrl(productConfig)}${urlSuffix}` : null;
    }
    return null;
  }

  private getProductEnvUrl(productConfig: OtherProductConfig): string {
    switch (env()) {
      case RuntimeEnvironment.TEST:
        return productConfig.endpoints.test;
      case RuntimeEnvironment.PROD:
        return productConfig.endpoints.prod;
      default:
        return productConfig.endpoints.dev;
    }
  }

  private setFavicon(): void {
    const config = InternalConfig.config[salesChannel()];
    if (isNotNullOrUndefined(config) && isNotNullOrUndefined(config.favicon)) {
      updateOrCreateFavicon(config.favicon);
    }
  }
}
